import { useState, useEffect, useCallback, memo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../styles/Navbar.css';
import { FaBars, FaTimes, FaShoppingCart, FaUser, FaSearch } from 'react-icons/fa';
import SearchBar from './navigation/SearchBar';
import MegaMenu from './navigation/MegaMenu';
import { useAuth } from '../contexts/AuthContext';

const Navbar = ({ toggleCart, cartItemCount = 0 }) => {
  const { isAuthenticated, isAdmin, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = useCallback(() => {
    logout();
    setMenuOpen(false);
    setShowMobileSearch(false);
    navigate('/');
  }, [logout, navigate]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMenuOpen(false);
    setShowMobileSearch(false);
  }, [location.pathname]);

  const toggleMenu = useCallback(() => {
    setMenuOpen(prev => !prev);
    setShowMobileSearch(false);
  }, []);

  const toggleMobileSearch = useCallback(() => {
    setShowMobileSearch(prev => !prev);
    setMenuOpen(false);
  }, []);

  const handleSignInClick = useCallback(() => {
    navigate('/auth?mode=login');
  }, [navigate]);

  const handleSearch = useCallback(
    (query, filters) => {
      if (!query?.trim()) {
        return;
      }

      const searchParams = new URLSearchParams({
        q: query.trim(),
        ...filters,
      });
      navigate(`/shop?${searchParams.toString()}`);
    },
    [navigate],
  );

  const handleCategoryClick = useCallback(
    category => {
      if (category) {
        navigate(`/shop?category=${category}`);
      }
    },
    [navigate],
  );

  return (
    <>
      {/* Main Navbar */}
      <nav className='enhanced-navbar'>
        <div className='navbar-container'>
          {/* Left Section - Brand */}
          <div className='navbar-brand'>
            <Link to='/' className='brand-link'>
              AmFashion
            </Link>
          </div>

          {/* Center Section - Navigation Only */}
          <div className='navbar-center'>
            {/* Desktop Navigation */}
            <div className='desktop-nav'>
              <Link to='/' className='nav-link'>
                Home
              </Link>
              <MegaMenu
                trigger='Shop'
                onCategoryClick={handleCategoryClick}
                className='nav-mega-menu'
              />
              <Link to='/customize-outfit' className='nav-link'>
                Customize
              </Link>
              <Link to='/coming-soon' className='nav-link'>
                Coming Soon
              </Link>
              {isAdmin && (
                <Link to='/admin' className='nav-link admin-link'>
                  Admin
                </Link>
              )}
            </div>
          </div>

          {/* Right Section - Actions */}
          <div className='navbar-actions'>
            {/* Mobile Search Toggle */}
            <button
              className='mobile-search-toggle'
              onClick={toggleMobileSearch}
              aria-label='Toggle search'
            >
              <FaSearch />
            </button>

            {/* User Account */}
            <div className='user-menu'>
              {isAuthenticated ? (
                <div className='user-dropdown'>
                  <button
                    className='user-button'
                    aria-label='User account menu'
                    aria-expanded='false'
                    aria-haspopup='true'
                  >
                    <FaUser aria-hidden='true' />
                    <span className='user-text'>Account</span>
                  </button>
                  <div className='user-dropdown-content' role='menu'>
                    <Link to='/account' className='dropdown-item' role='menuitem'>
                      My Account
                    </Link>
                    <Link to='/account?tab=orders' className='dropdown-item' role='menuitem'>
                      Orders
                    </Link>
                    <button
                      onClick={handleSignOut}
                      className='dropdown-item sign-out'
                      role='menuitem'
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={handleSignInClick}
                  className='sign-in-button'
                  aria-label='Sign in to your account'
                >
                  <FaUser aria-hidden='true' />
                  <span>Sign In</span>
                </button>
              )}
            </div>

            {/* Shopping Cart */}
            <button
              className='cart-button'
              onClick={toggleCart}
              aria-label={`Shopping cart with ${cartItemCount} ${cartItemCount === 1 ? 'item' : 'items'}`}
            >
              <div className='cart-icon-wrapper'>
                <FaShoppingCart aria-hidden='true' />
                {cartItemCount > 0 && (
                  <span className='cart-badge' aria-label={`${cartItemCount} items in cart`}>
                    {cartItemCount > 99 ? '99+' : cartItemCount}
                  </span>
                )}
              </div>
              <span className='cart-text'>Cart</span>
            </button>

            {/* Mobile Menu Toggle */}
            <button
              className='mobile-menu-toggle'
              onClick={toggleMenu}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              aria-controls='mobile-nav-menu'
            >
              {menuOpen ? <FaTimes aria-hidden='true' /> : <FaBars aria-hidden='true' />}
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        <div className={`mobile-search-container ${showMobileSearch ? 'show' : ''}`}>
          <SearchBar
            onSearch={handleSearch}
            placeholder='Search products, brands, styles...'
            className='mobile-search'
            showFilters
          />
        </div>

        {/* Mobile Navigation Menu */}
        <div
          id='mobile-nav-menu'
          className={`mobile-nav-menu ${menuOpen ? 'show' : ''}`}
          role='navigation'
          aria-label='Mobile navigation'
        >
          <div className='mobile-nav-content'>
            <Link to='/' className='mobile-nav-link' onClick={() => setMenuOpen(false)}>
              Home
            </Link>
            <Link to='/shop' className='mobile-nav-link' onClick={() => setMenuOpen(false)}>
              Shop
            </Link>

            {/* Mobile Categories */}
            <div className='mobile-categories'>
              <div className='mobile-category-title'>Categories</div>
              <Link
                to='/shop?category=Dresses'
                className='mobile-nav-sublink'
                onClick={() => setMenuOpen(false)}
              >
                Dresses
              </Link>
              <Link
                to='/shop?category=Tops'
                className='mobile-nav-sublink'
                onClick={() => setMenuOpen(false)}
              >
                Tops
              </Link>
              <Link
                to='/shop?category=Bottoms'
                className='mobile-nav-sublink'
                onClick={() => setMenuOpen(false)}
              >
                Bottoms
              </Link>
              <Link
                to='/shop?category=Outerwear'
                className='mobile-nav-sublink'
                onClick={() => setMenuOpen(false)}
              >
                Outerwear
              </Link>
            </div>

            <Link
              to='/customize-outfit'
              className='mobile-nav-link'
              onClick={() => setMenuOpen(false)}
            >
              Customize
            </Link>
            <Link to='/coming-soon' className='mobile-nav-link' onClick={() => setMenuOpen(false)}>
              Coming Soon
            </Link>

            {isAdmin && (
              <Link
                to='/admin'
                className='mobile-nav-link admin-link'
                onClick={() => setMenuOpen(false)}
              >
                Admin
              </Link>
            )}

            {/* Mobile User Actions */}
            <div className='mobile-user-actions'>
              {isAuthenticated ? (
                <>
                  <Link
                    to='/account'
                    className='mobile-nav-link'
                    onClick={() => setMenuOpen(false)}
                  >
                    My Account
                  </Link>
                  <Link
                    to='/account?tab=orders'
                    className='mobile-nav-link'
                    onClick={() => setMenuOpen(false)}
                  >
                    Orders
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className='mobile-nav-button'
                    aria-label='Sign out of your account'
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <button
                  onClick={handleSignInClick}
                  className='mobile-nav-button primary'
                  aria-label='Sign in to your account'
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default memo(Navbar);
